import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import useAuth from '../../hooks/useAuth';
import { apiFetch } from '../../api/http';

const SPECTRA_IMPORT_OPEN_EVENT = 'syn:spectra-import-open';

export function openSpectraImportDialog() {
  window.dispatchEvent(new CustomEvent(SPECTRA_IMPORT_OPEN_EVENT));
}

export type NirEquipment = {
  idmaquinaNIR: string;
  nombre: string;
  localizacion: string;
  numserie: string;
  modelo: string;
  fabricante: string;
  resolucion: string;
  notas: string;
  longitudonda: string;
  idempresa: string;
  empresa: string;
  idgrupoempresa: string;
  grupoempresa: string;
};

function asString(value: unknown): string {
  if (value == null || typeof value === 'object') return '';
  const text = String(value).trim();
  return text && text !== 'undefined' && text !== 'null' ? text : '';
}

function field(raw: Record<string, unknown>, key: string): unknown {
  if (raw[key] != null) return raw[key];
  const target = key.toLowerCase();
  return Object.entries(raw).find(([k]) => k.toLowerCase() === target)?.[1];
}

function namedValue(value: unknown): string {
  if (value && typeof value === 'object') {
    return asString((value as Record<string, unknown>).nombre);
  }
  return asString(value);
}

export function mapEquipment(raw: Record<string, unknown>): NirEquipment {
  return {
    idmaquinaNIR: asString(field(raw, 'idmaquinaNIR')),
    nombre: asString(field(raw, 'nombre')),
    localizacion: asString(field(raw, 'localizacion')),
    numserie: asString(field(raw, 'numserie')),
    modelo: asString(field(raw, 'modelo')),
    fabricante: namedValue(field(raw, 'fabricante')),
    resolucion: namedValue(field(raw, 'resolucion')),
    notas: asString(field(raw, 'notas') ?? field(raw, 'observaciones')),
    longitudonda: asString(field(raw, 'longitudonda')),
    idempresa: asString(field(raw, 'idempresa')),
    empresa: namedValue(field(raw, 'empresa')),
    idgrupoempresa: asString(field(raw, 'idgrupoempresa')),
    grupoempresa: namedValue(field(raw, 'grupoempresa'))
  };
}

export function asEquipmentList(json: unknown): Record<string, unknown>[] {
  if (!Array.isArray(json)) return [];
  return json.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
}

// TODO: replace with the backend create/update equipment endpoints once they are available.
const mockEquipmentByCompany = new Map<string, NirEquipment[]>();

export async function fetchMockEquipment(companyId: string, signal?: AbortSignal): Promise<NirEquipment[]> {
  const cached = mockEquipmentByCompany.get(companyId);
  if (cached) return cached;

  const res = await apiFetch(`${import.meta.env.VITE_APP_API_URL}/equipment/companygroup/${companyId}`, { signal });
  if (!res.ok) throw new Error('equipment');
  const list = asEquipmentList(await res.json())
    .map(mapEquipment)
    .filter((eq) => eq.idmaquinaNIR);
  mockEquipmentByCompany.set(companyId, list);
  return list;
}

export function setMockEquipment(companyId: string, list: NirEquipment[]) {
  mockEquipmentByCompany.set(companyId, list);
}

function equipmentHeading(eq: Pick<NirEquipment, 'nombre' | 'grupoempresa' | 'localizacion'>) {
  return [eq.nombre, eq.grupoempresa, eq.localizacion].filter(Boolean).join(' ');
}

function equipmentDetail(eq: Pick<NirEquipment, 'modelo' | 'numserie' | 'resolucion'>) {
  const detail = `${eq.modelo} (${eq.numserie})`;
  return eq.resolucion ? `${detail} · ${eq.resolucion}` : detail;
}

type ValidationIssue = {
  id: string;
  row: number;
  sample: string;
  field: string;
  error: string;
  action: string;
  severity: 'blocking' | 'warning';
  resolved: boolean;
};

function asNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function asObjectList(json: unknown, keys: string[]): Record<string, unknown>[] {
  if (Array.isArray(json)) {
    return json.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
  }
  if (!json || typeof json !== 'object') return [];
  const obj = json as Record<string, unknown>;
  for (const key of keys) {
    const nested = obj[key];
    if (Array.isArray(nested)) {
      return nested.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
    }
  }
  return [];
}

function mapValidationIssue(raw: Record<string, unknown>, index: number): ValidationIssue {
  const severityRaw = asString(field(raw, 'severity') || field(raw, 'tipo')).toLowerCase();
  return {
    id: asString(field(raw, 'id')) || `v${index + 1}`,
    row: asNumber(field(raw, 'row') ?? field(raw, 'fila')) ?? 0,
    sample: asString(field(raw, 'sample') ?? field(raw, 'muestra') ?? field(raw, 'sampleId')),
    field: asString(field(raw, 'field') ?? field(raw, 'campo')),
    error: asString(field(raw, 'error') ?? field(raw, 'mensaje') ?? field(raw, 'message')),
    action: asString(field(raw, 'action') ?? field(raw, 'accion') ?? field(raw, 'recommendedAction')),
    severity: severityRaw === 'warning' || severityRaw === 'aviso' ? 'warning' : 'blocking',
    resolved: false
  };
}

function readCount(json: unknown, keys: string[]): number | null {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return null;
  const obj = json as Record<string, unknown>;
  for (const key of keys) {
    const n = asNumber(field(obj, key));
    if (n != null) return n;
  }
  return null;
}

const STEP_KEYS = ['equipment', 'file', 'validation', 'result'] as const;
const ACCEPTED_EXTENSIONS = ['.csv', '.nir', '.sp', '.spc', '.jdx', '.json', '.opus', '.spa'] as const;
const CSV_TEMPLATE = `Company Group;Product Code;Batch Number;Serial Number;Registration Date;Absorbances`;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

const footerButtonSx = {
  minWidth: 132,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  px: 2.5
} as const;

// ==============================|| SPECTRA IMPORT ||============================== //

export default function SpectraImport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.idcompanyGroup || '';
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [search, setSearch] = useState('');
  const [equipment, setEquipment] = useState<NirEquipment[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [equipmentError, setEquipmentError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [spectraFound, setSpectraFound] = useState(0);
  const [validCount, setValidCount] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [showCsvHelp, setShowCsvHelp] = useState(false);
  const validateAbortRef = useRef<AbortController | null>(null);

  const abortValidate = () => {
    validateAbortRef.current?.abort();
    validateAbortRef.current = null;
  };

  const resetState = () => {
    setActiveStep(0);
    setSearch('');
    setSelectedId(null);
    setImportFile(null);
    setDragOver(false);
    setFileError(null);
    setIssues([]);
    setIsValidating(false);
    setValidationError(false);
    setSpectraFound(0);
    setValidCount(0);
    setImportedCount(0);
    setShowCsvHelp(false);
    abortValidate();
  };

  useEffect(() => {
    const onOpen = () => {
      resetState();
      setOpen(true);
    };
    window.addEventListener(SPECTRA_IMPORT_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(SPECTRA_IMPORT_OPEN_EVENT, onOpen);
      abortValidate();
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
    resetState();
  };

  const handleDialogClose = (_event: unknown, reason: string) => {
    if (reason === 'backdropClick') return;
    handleClose();
  };

  useEffect(() => {
    if (!open) return;
    if (!companyId) {
      setEquipment([]);
      setEquipmentError(true);
      setLoadingEquipment(false);
      return;
    }

    const controller = new AbortController();
    setEquipmentError(false);
    setLoadingEquipment(true);

    (async () => {
      try {
        setEquipment(await fetchMockEquipment(companyId, controller.signal));
      } catch {
        if (controller.signal.aborted) return;
        setEquipmentError(true);
        setEquipment([]);
      } finally {
        if (!controller.signal.aborted) setLoadingEquipment(false);
      }
    })();

    return () => controller.abort();
  }, [open, companyId]);

  const selectedEquipment = useMemo(
    () => equipment.find((eq) => eq.idmaquinaNIR === selectedId) ?? null,
    [equipment, selectedId]
  );

  const filteredEquipment = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return equipment;
    return equipment.filter((eq) =>
      [eq.nombre, eq.modelo, eq.numserie, eq.resolucion, eq.localizacion, eq.grupoempresa].some((v) => v.toLowerCase().includes(q))
    );
  }, [equipment, search]);

  const applyFile = (file: File | undefined) => {
    if (!file) return;
    if (!isAcceptedFile(file)) {
      setImportFile(null);
      setFileError(t('pages.spectraImport.file.invalidFormat'));
      return;
    }
    setFileError(null);
    setImportFile(file);
    setIssues([]);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    applyFile(event.dataTransfer.files?.[0]);
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'spectra-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const unresolvedIssues = useMemo(() => issues.filter((i) => !i.resolved), [issues]);
  const blockingCount = useMemo(
    () => unresolvedIssues.filter((i) => i.severity === 'blocking').length,
    [unresolvedIssues]
  );
  const warningCount = useMemo(
    () => unresolvedIssues.filter((i) => i.severity === 'warning').length,
    [unresolvedIssues]
  );
  const errorCount = blockingCount;

  const canContinue =
    activeStep === 0
      ? Boolean(selectedId)
      : activeStep === 1
        ? Boolean(importFile)
        : activeStep === 2
          ? !isValidating && !validationError && errorCount === 0
          : false;

  const handleContinue = () => {
    if (!canContinue) return;
    if (activeStep === 1) {
      if (!importFile) return;
      abortValidate();
      setIssues([]);
      setValidationError(false);
      setSpectraFound(0);
      setValidCount(0);
      setIsValidating(true);
      setActiveStep(2);

      const controller = new AbortController();
      validateAbortRef.current = controller;
      const fileToValidate = importFile;

      (async () => {
        try {
          const form = new FormData();
          form.append('file', fileToValidate, fileToValidate.name);
          const res = await apiFetch(`${import.meta.env.VITE_APP_API_URL}/nirfiles/validate`, {
            method: 'POST',
            headers: { file: fileToValidate.name },
            body: form,
            signal: controller.signal
          });
          const json = await res.json().catch(() => null);
          if (controller.signal.aborted) return;
          if (!res.ok) throw new Error('validate');

          const mapped = asObjectList(json, ['issues', 'errors', 'validations', 'items', 'data']).map(mapValidationIssue);
          const found = readCount(json, ['total', 'count', 'found', 'spectraFound', 'espectros']) ?? mapped.length;
          const blocking = mapped.filter((issue) => issue.severity === 'blocking').length;
          const valid = readCount(json, ['valid', 'validCount', 'espectrosValidos']) ?? Math.max(0, found - blocking);

          setIssues(mapped);
          setSpectraFound(found);
          setValidCount(valid);
        } catch {
          if (controller.signal.aborted) return;
          setValidationError(true);
          setIssues([]);
          setSpectraFound(0);
          setValidCount(0);
        } finally {
          if (!controller.signal.aborted) setIsValidating(false);
        }
      })();
      return;
    }
    if (activeStep === 2) {
      setImportedCount(validCount);
    }
    setActiveStep((s) => Math.min(s + 1, STEP_KEYS.length - 1));
  };

  const handleBack = () => {
    abortValidate();
    setIsValidating(false);
    setValidationError(false);
    if (activeStep === 2) {
      setImportFile(null);
      setFileError(null);
      setIssues([]);
      setSpectraFound(0);
      setValidCount(0);
    }
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleViewImported = () => {};

  const handleImportAnother = () => {
    abortValidate();
    setSearch('');
    setSelectedId(null);
    setImportFile(null);
    setFileError(null);
    setIssues([]);
    setValidationError(false);
    setSpectraFound(0);
    setValidCount(0);
    setImportedCount(0);
    setShowCsvHelp(false);
    setActiveStep(0);
  };

  const resultButtonSx = {
    ...footerButtonSx,
    minWidth: 0
  } as const;

  const primaryActionLabel =
    activeStep === 1
      ? t('pages.spectraImport.file.validate')
      : activeStep === 2
        ? t('pages.spectraImport.validation.importAction', { count: validCount })
        : t('pages.spectraImport.continue');

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: '55vw',
            maxWidth: 1280,
            height: '80vh',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden'
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          position: 'relative',
          pr: 6,
          py: 2.25,
          fontWeight: 600,
          fontSize: 20,
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        {t('pages.spectraImport.title')}
        <IconButton
          aria-label={t('actions.close')}
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          px: 3,
          pt: '24px !important',
          pb: 0,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {STEP_KEYS.map((key, index) => {
            const isActive = index === activeStep;
            const isDone = index < activeStep;
            const isLast = index === STEP_KEYS.length - 1;
            return (
              <Box key={key} sx={{ display: 'contents' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexShrink: 0, cursor: index <= activeStep ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (index <= activeStep) {
                      if (activeStep === 2 && index < 2) {
                        abortValidate();
                        setIsValidating(false);
                        setValidationError(false);
                        setImportFile(null);
                        setFileError(null);
                        setIssues([]);
                        setSpectraFound(0);
                        setValidCount(0);
                      } else if (index < 2) {
                        abortValidate();
                        setIsValidating(false);
                        setValidationError(false);
                      }
                      setActiveStep(index);
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: isActive || isDone ? 'primary.main' : 'grey.300',
                      color: 'common.white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1
                    }}
                  >
                    {isDone ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 14,
                      color: isActive ? 'text.primary' : 'grey.500',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {t(`pages.spectraImport.steps.${key}`)}
                  </Typography>
                </Stack>
                {!isLast && (
                  <Box
                    sx={{
                      flex: 1,
                      height: '1px',
                      bgcolor: isDone ? 'primary.main' : 'grey.300',
                      mx: 1.5,
                      minWidth: 12
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {activeStep === 0 && (
          <>
            <Typography sx={{ color: 'grey.600', fontSize: 15 }}>
              {t('pages.spectraImport.equipment.instruction')}
            </Typography>

            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('pages.spectraImport.equipment.searchPlaceholder')}
              fullWidth
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'grey.500' }} />
                    </InputAdornment>
                  )
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'auto'
              }}
            >
              {loadingEquipment && equipment.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : equipmentError ? (
                <Box sx={{ px: 2, py: 4 }}>
                  <Typography sx={{ color: 'error.main', textAlign: 'center' }}>
                    {t('pages.spectraImport.equipment.loadError')}
                  </Typography>
                </Box>
              ) : (
                <List disablePadding sx={{ '& *::selection': { backgroundColor: '#0BA26F' } }}>
                  {filteredEquipment.map((eq, index) => {
                    const selected = selectedId === eq.idmaquinaNIR;
                    return (
                      <Box key={eq.idmaquinaNIR}>
                        {index > 0 && <Divider sx={{ borderColor: 'lightgrey' }} />}
                        <ListItemButton
                          selected={selected}
                          onClick={() => setSelectedId(eq.idmaquinaNIR)}
                          sx={{
                            py: 1.5,
                            px: 2,
                            gap: 1,
                            bgcolor: index % 2 === 0 ? '#e8f5e9' : 'transparent',
                            '&:hover': { bgcolor: '#A8DAC0' },
                            '&:hover .MuiTypography-root, &:hover .MuiSvgIcon-root, &.Mui-selected .MuiTypography-root, &.Mui-selected .MuiSvgIcon-root':
                              { color: '#01392C' },
                            '&.Mui-selected': {
                              bgcolor: '#C5EBD9',
                              '&:hover': { bgcolor: '#8BCEAE' }
                            },
                            '& .MuiRadio-root.Mui-checked .MuiSvgIcon-root': { color: '#0BA26F' }
                          }}
                        >
                          <Radio
                            checked={selected}
                            tabIndex={-1}
                            disableRipple
                            color="primary"
                            sx={{ p: 0.5 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 15, color: 'text.primary' }}>
                              {equipmentHeading(eq)}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
                              {equipmentDetail(eq)}
                            </Typography>
                          </Box>
                          <ChevronRightIcon sx={{ color: 'grey.400', fontSize: 22 }} />
                        </ListItemButton>
                      </Box>
                    );
                  })}
                  {filteredEquipment.length === 0 && (
                    <Box sx={{ px: 2, py: 4 }}>
                      <Typography sx={{ color: 'grey.500', textAlign: 'center' }}>
                        {t(search.trim() ? 'pages.spectraImport.equipment.empty' : 'pages.spectraImport.equipment.none')}
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>

            <Button
              variant="text"
              color="primary"
              onClick={() => {
                handleClose();
                navigate('/spectra/equipments');
              }}
              startIcon={<TuneOutlinedIcon sx={{ fontSize: 22 }} />}
              sx={{
                alignSelf: 'flex-start',
                px: 0,
                minWidth: 0,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 22 }
              }}
            >
              {t('pages.spectraImport.equipment.manage')}
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Typography sx={{ color: 'grey.600', fontSize: 15 }}>
              {t('pages.spectraImport.file.uploadingFor')}{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {selectedEquipment ? equipmentHeading(selectedEquipment) : ''}
              </Box>
              <Box component="span" sx={{ color: 'text.primary' }}>
                {selectedEquipment ? ` ${equipmentDetail(selectedEquipment)}` : ''}
              </Box>
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              hidden
              onChange={(e) => {
                applyFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />

            <Box
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(false);
              }}
              onDrop={handleDrop}
              sx={{
                flex: 1,
                minHeight: 120,
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                bgcolor: dragOver ? 'grey.200' : 'grey.100',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.25,
                px: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border-color 150ms ease, background-color 150ms ease'
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 46, color: 'primary.main' }} />
              <Typography sx={{ fontWeight: 500, fontSize: 16, color: 'text.primary' }}>
                {t('pages.spectraImport.file.dropTitle')}
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'grey.500' }}>
                {t('pages.spectraImport.file.supportedFormats')}
              </Typography>
              {fileError && (
                <Typography sx={{ fontSize: 14, color: 'error.main' }}>{fileError}</Typography>
              )}
            </Box>

            {importFile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.75,
                  py: 1.25,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: 'rgb(255, 255, 255)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <DescriptionOutlined sx={{ color: 'primary.main', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    title={importFile.name}
                    sx={{ fontWeight: 500, fontSize: 14, color: 'text.primary', lineHeight: 1.3 }}
                  >
                    {importFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'grey.500', lineHeight: 1.4 }}>
                    {formatFileSize(importFile.size)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label={t('pages.spectraImport.file.removeFile')}
                  onClick={() => {
                    setImportFile(null);
                    setFileError(null);
                  }}
                  sx={{ color: 'grey.400', '&:hover': { color: 'grey.600' } }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}

            <Stack spacing={1} sx={{ pb: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                variant="text"
                color="primary"
                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 24 }} />}
                onClick={handleDownloadTemplate}
                sx={{
                  px: 0,
                  minWidth: 0,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 24 }
                }}
              >
                {t('pages.spectraImport.file.downloadTemplate')}
              </Button>
              <Button
                variant="text"
                startIcon={<InfoOutlinedIcon sx={{ fontSize: 24 }} />}
                onClick={() => setShowCsvHelp((v) => !v)}
                sx={{
                  px: 0,
                  minWidth: 0,
                  fontWeight: 500,
                  color: showCsvHelp ? 'primary.main' : 'grey.600',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 24 }
                }}
              >
                {t('pages.spectraImport.file.howToFill')}
              </Button>
            </Stack>

            {showCsvHelp && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2
                }}
              >
                <Typography sx={{ fontSize: 14, color: 'grey.700', mb: 1 }}>
                  {t('pages.spectraImport.file.howToFillIntro')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'grey.700' }}>
                  {(['sampleId', 'product', 'batch', 'spectrum'] as const).map((key, index, arr) => (
                    <Box component="li" key={key} sx={{ fontSize: 14, mb: index === arr.length - 1 ? 0 : 0.5 }}>
                      {t(`pages.spectraImport.file.howToFillItems.${key}`)}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            </Stack>
          </>
        )}

        {activeStep === 2 && isValidating && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              textAlign: 'center'
            }}
          >
            <CircularProgress size={52} thickness={4} sx={{ color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 600, fontSize: 16, color: 'text.primary' }}>
              {t('pages.spectraImport.validation.reading')}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'grey.500' }}>
              {t('pages.spectraImport.validation.readingHint')}
            </Typography>
          </Box>
        )}

        {activeStep === 2 && !isValidating && validationError && (
          <Box sx={{ px: 2, py: 6 }}>
            <Typography sx={{ color: 'error.main', textAlign: 'center' }}>
              {t('pages.spectraImport.validation.loadError')}
            </Typography>
          </Box>
        )}

        {activeStep === 2 && !isValidating && !validationError && (
          <>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <CheckCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: 'text.primary' }}>
                  {t('pages.spectraImport.validation.fileReadOk')}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 14, color: 'grey.700', pl: 4 }}>
                {t('pages.spectraImport.validation.found', { count: spectraFound })}
                {' · '}
                <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {t('pages.spectraImport.validation.valid', { count: validCount })}
                </Box>
                {' · '}
                <Box component="span" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {t('pages.spectraImport.validation.withErrors', { count: errorCount })}
                </Box>{' '}
                {t('pages.spectraImport.validation.breakdown', {
                  blocking: blockingCount,
                  warnings: warningCount
                })}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2
              }}
            >
              <Table stickyHeader size="small" sx={{ '& *::selection': { backgroundColor: '#0BA26F' } }}>
                <TableHead>
                  <TableRow>
                    {(
                      ['row', 'sample', 'field', 'error', 'recommendedAction', 'status'] as const
                    ).map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'grey.500',
                          bgcolor: 'background.paper',
                          borderBottomColor: 'grey.300',
                          whiteSpace: 'nowrap',
                          py: 1.25
                        }}
                      >
                        {t(`pages.spectraImport.validation.columns.${col}`)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unresolvedIssues.map((issue) => (
                    <TableRow
                      key={issue.id}
                      hover
                      sx={{
                        '& td': { borderBottom: '1px solid', borderBottomColor: 'lightgrey' },
                        '&:nth-of-type(odd)': { bgcolor: '#e8f5e9' },
                        '&.MuiTableRow-hover:hover': { bgcolor: '#A8DAC0' },
                        '&.MuiTableRow-hover:hover td': { color: '#01392C' }
                      }}
                    >
                      <TableCell sx={{ color: 'grey.500', fontSize: 13, width: 56 }}>{issue.row}</TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{issue.sample}</TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{issue.field}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{issue.error}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{issue.action}</TableCell>
                      <TableCell sx={{ width: 110 }}>
                        <Chip
                          size="small"
                          label={
                            issue.severity === 'blocking'
                              ? t('pages.spectraImport.validation.statusBlocking')
                              : t('pages.spectraImport.validation.statusWarning')
                          }
                          sx={
                            issue.severity === 'blocking'
                              ? { fontWeight: 600, bgcolor: '#FDECEC', color: '#C62828' }
                              : { fontWeight: 600, bgcolor: '#FFF4E5', color: '#B26A00' }
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {errorCount > 0 && (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 2,
                  bgcolor: '#FFF8E1',
                  color: '#B26A00',
                  '& .MuiAlert-icon': { color: '#B26A00' },
                  '& .MuiAlert-message': { color: '#B26A00', fontSize: 14}
                }}
              >
                {t('pages.spectraImport.validation.blockingBanner', { count: errorCount })}
              </Alert>
            )}
          </>
        )}

        {activeStep === 3 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 2.5,
              px: 2
            }}
          >
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                bgcolor: 'rgba(11, 162, 111, 0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
                <CheckCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 46 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 22, color: 'text.primary', mb: 1 }}>
                {t('pages.spectraImport.result.title')}
              </Typography>
              <Typography sx={{ fontSize: 15, color: 'grey.600' }}>
                {t('pages.spectraImport.result.summary', { count: importedCount })}{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {selectedEquipment ? `${equipmentHeading(selectedEquipment)} ${equipmentDetail(selectedEquipment)}` : ''}
                </Box>
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<VisibilityOutlinedIcon />}
                onClick={handleViewImported}
                sx={resultButtonSx}
              >
                {t('pages.spectraImport.result.viewImported')}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ReplayOutlinedIcon />}
                onClick={handleImportAnother}
                sx={{
                  ...resultButtonSx,
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' }
                }}
              >
                {t('pages.spectraImport.result.importAnother')}
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          display: isValidating || activeStep === 3 ? 'none' : 'flex',
          justifyContent: activeStep > 0 ? 'space-between' : 'flex-end',
          px: 3,
          pt: 1,
          pb: 2
        }}
      >
        {activeStep > 0 && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleBack}
            startIcon={<ChevronLeftIcon />}
            sx={{
              ...footerButtonSx,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' }
            }}
          >
            {t('pages.spectraImport.back')}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={!canContinue}
          onClick={handleContinue}
          startIcon={activeStep === 2 ? <PlaylistAddCheckIcon sx={{ fontSize: 22 }} /> : <ChevronRightIcon />}
          sx={{
            ...footerButtonSx,
            ...(activeStep === 2 && {
              '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 22 }
            })
          }}
        >
          {primaryActionLabel}
        </Button>
      </Box>
    </Dialog>
  );
}
